// History (the Shipping hub's tab; the old "Shipped Items"): ONE copy of its reads, the reprint paths, the part-label
// configuration and the POST /reports/batch_part_labels, for the desktop (pages/ShippingQueuePage.jsx, LoadsHistory)
// and the phone (mobile/screens/shipping/HistoryTab.jsx), moved out of the page 2026-10-29 with the desktop's
// requests unchanged.
//
// Printing: the desktop opens every PDF. A phone opens the manifest in its PDF viewer (GET) and sends container /
// part labels (GET) to the shop print queue at its label station; the batch part labels are POST-only (the print
// agent fetches GETs only), so a phone gets that PDF back to share.
import { v4 as uuidv4 } from 'uuid';
import { assemblyCountFromPrimarySpace } from '../../utils/assemblyMath';
import { listLoads } from '../signing/signRequestsApi';
import { formatShipDate, lineAssemblyPpq, sortRows } from './shipByJob';

export { sortRows };

// --- loads (LoadsHistory) ------------------------------------------------------------------------

export const HISTORY_DAYS = 14;

export const LOAD_KINDS = [
  { id: '', label: 'All loads' },
  { id: 'delivery', label: 'We deliver' },
  { id: 'carrier', label: 'Carrier' },
  { id: 'job', label: 'Ship by job' },
];

/** `days` before `now`, as a local 'YYYY-MM-DD'. */
export function sinceDate(days, now = new Date()) {
  const d = new Date(now.getTime() - days * 86400000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** GET /shipping/loads?kind=&since= for the last HISTORY_DAYS -> {ok, loads, message, missing}. `missing`: an API
 *  without loads (404 / 405), where History is the old lists only. */
export async function loadRecentLoads(apiBase, kind = '', now = new Date()) {
  const r = await listLoads(apiBase, { kind: kind || null, since: sinceDate(HISTORY_DAYS, now) });
  return { ok: r.ok, loads: r.loads, message: r.ok ? '' : r.message, missing: !r.ok && [404, 405].includes(r.status) };
}

/** A load's document opens with the API key (a path on the API) unless it is a full link elsewhere. */
export function isExternalUrl(url) {
  return /^https?:\/\//i.test(String(url || ''));
}

// --- shipped items -------------------------------------------------------------------------------

/** The History filters: {startDate, endDate (Date | 'YYYY-MM-DD' | null), customerId, productId, customerPo,
 *  customerSo, salesOrder}. */
export const EMPTY_HISTORY_FILTERS = Object.freeze({
  startDate: null, endDate: null, customerId: '', productId: '', customerPo: '', customerSo: '', salesOrder: '',
});

/** By Task: the query string for GET /queue/shipping_details?shipped=true&... */
export function shippedQueryParams(f = EMPTY_HISTORY_FILTERS) {
  const params = new URLSearchParams({ shipped: 'true' });
  if (f.startDate) params.append('start_date', formatShipDate(f.startDate));
  if (f.endDate) params.append('end_date', formatShipDate(f.endDate));
  if (f.customerId) params.append('customer_id', f.customerId);
  if (String(f.productId || '').trim()) params.append('product_id', String(f.productId).trim());
  if (String(f.customerPo || '').trim()) params.append('customer_po', String(f.customerPo).trim());
  if (String(f.customerSo || '').trim()) params.append('customer_so', String(f.customerSo).trim());
  return params.toString();
}

/** By Shipment: the query string for GET /shipping/shipment_batches?... */
export function shipmentBatchParams(f = EMPTY_HISTORY_FILTERS) {
  const params = new URLSearchParams();
  if (f.startDate) params.append('start_date', formatShipDate(f.startDate));
  if (f.endDate) params.append('end_date', formatShipDate(f.endDate));
  if (f.customerId) params.append('customer_id', f.customerId);
  if (String(f.salesOrder || '').trim()) params.append('sales_order_number', String(f.salesOrder).trim());
  return params.toString();
}

/** By Task: shipped lines -> {ok, rows, message}. `message` is the sentence to show on a failure. */
export async function loadShippedItems(apiBase, filters) {
  try {
    const res = await fetch(`${apiBase}/queue/shipping_details?${shippedQueryParams(filters)}`);
    if (!res.ok) throw new Error('Failed to load shipped items');
    const data = await res.json();
    return { ok: true, rows: Array.isArray(data) ? data : [], message: '' };
  } catch (err) {
    console.error('Error loading shipped items:', err);
    return { ok: false, rows: [], message: 'Error loading shipped items: ' + err.message };
  }
}

/** By Shipment: shipment batches -> {ok, rows, message}. */
export async function loadShipmentBatches(apiBase, filters) {
  try {
    const res = await fetch(`${apiBase}/shipping/shipment_batches?${shipmentBatchParams(filters)}`);
    if (!res.ok) throw new Error('Failed to load shipment batches');
    const data = await res.json();
    return { ok: true, rows: Array.isArray(data) ? data : [], message: '' };
  } catch (err) {
    console.error('Error loading shipment batches:', err);
    return { ok: false, rows: [], message: 'Error loading shipment batches: ' + err.message };
  }
}

/** A shipped line's quantity as shown: finished assemblies for an assembly line (ppq > 1), else pieces. */
export function shippedQty(line) {
  return assemblyCountFromPrimarySpace(line.quantity_shipped, lineAssemblyPpq(line)) || 0;
}

// --- reprints ------------------------------------------------------------------------------------

export const manifestBatchPath = (groupId) => `/reports/manifest/batch/${groupId}`;
export const palletLabelsBatchPath = (groupId) => `/reports/pallet_labels/batch/${groupId}`;
export const partLabelsBatchPath = (groupId) => `/reports/part_labels/batch/${groupId}`;

export const NO_BATCH_TEXT = 'This item does not have a Batch ID (legacy data).';

/** A shipment's labels as print-queue jobs (a phone): kind + what for palletActions.printLabelsAt. */
export function batchLabelJob(groupId, which, batch = null) {
  const so = batch && batch.sales_order_number ? `${batch.sales_order_number} ` : '';
  if (which === 'parts') return { path: partLabelsBatchPath(groupId), kind: 'part_labels', what: `${so}part labels` };
  return { path: palletLabelsBatchPath(groupId), kind: 'pallet_labels', what: `${so}container labels` };
}

// --- part labels (By Task) -------------------------------------------------------------------------

/** One label batch row: {id, qty, count}. */
export function newLabelBatch(qty = 1, count = 1) {
  return { id: uuidv4(), qty, count };
}

/** The ticked shipped lines -> the label configuration: one entry per part (its first ticked line), one batch of
 *  [shipped qty (assemblies for an assembly line), 1 label]. */
export function labelConfigFor(lines, selectedTaskIds) {
  const partsToPrint = new Map();
  (lines || []).forEach((line) => {
    if (!selectedTaskIds[line.task_id] || partsToPrint.has(line.part_id)) return;
    partsToPrint.set(line.part_id, {
      part_id: line.part_id,
      product_id: line.product_id,
      description: line.description,
      batches: [newLabelBatch(assemblyCountFromPrimarySpace(line.quantity_shipped, lineAssemblyPpq(line)) || 1, 1)],
    });
  });
  return Array.from(partsToPrint.values());
}

export function addLabelBatch(config, partId) {
  return config.map((part) => (part.part_id === partId ? { ...part, batches: [...part.batches, newLabelBatch()] } : part));
}

export function removeLabelBatch(config, partId, batchId) {
  return config.map((part) => (part.part_id === partId ? { ...part, batches: part.batches.filter((b) => b.id !== batchId) } : part));
}

export function updateLabelBatch(config, partId, batchId, field, value) {
  return config.map((part) => (part.part_id === partId
    ? { ...part, batches: part.batches.map((b) => (b.id === batchId ? { ...b, [field]: value } : b)) }
    : part));
}

/** The POST /reports/batch_part_labels body: parts with at least one batch of 1+ labels. */
export function partLabelsPayload(config) {
  return {
    parts: (config || []).map((part) => ({
      part_id: part.part_id,
      batches: part.batches.map((batch) => ({
        quantity_on_label: parseInt(batch.qty, 10) || 0,
        label_count: parseInt(batch.count, 10) || 0,
      })).filter((b) => b.label_count > 0),
    })).filter((p) => p.batches.length > 0),
  };
}

export const NO_LABELS_TEXT = 'No labels configured to print.';

/** POST /reports/batch_part_labels -> {ok, blob, message}. `message` is the sentence to show when not ok. */
export async function fetchBatchPartLabels(apiBase, config) {
  const payload = partLabelsPayload(config);
  if (payload.parts.length === 0) return { ok: false, blob: null, message: NO_LABELS_TEXT, empty: true };
  try {
    const res = await fetch(`${apiBase}/reports/batch_part_labels`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to generate PDF labels.');
    const blob = await res.blob();
    return { ok: true, blob, message: '' };
  } catch (err) {
    console.error('Error printing labels:', err);
    return { ok: false, blob: null, message: 'Error printing labels: ' + err.message };
  }
}
